/**
 * @architecture-header 1
 * @layer module
 * @domain notifications
 * @responsibility Optimistically mark inbox items read and persist real notification rows.
 * @state-owner TanStack Query cache
 * @allowed-imports app environment; same module query; contracts; TanStack Query
 * @forbidden routes; app bootstrap; direct Supabase client; localStorage
 * @public-api via modules/notifications/index.ts
 * @side-effects authenticated notification update outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InboxItem } from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { type InboxAudience, type InboxResponse, inboxKeys } from './inbox.queries';

const markResultSchema = z.object({
  markedCount: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
});

function isServerRow(item: Pick<InboxItem, 'id'>): boolean {
  return item.id.startsWith('notif-');
}

export function useMarkInboxRead(activeContextId: string, audience: InboxAudience | undefined) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const queryKey = inboxKeys.list(activeContextId, audience);
  return useMutation({
    mutationFn: async (item: InboxItem) => {
      if (environment.demoMode || !isServerRow(item)) return null;
      const notificationId = item.id.slice('notif-'.length);
      return markResultSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/v1/me/notifications/${encodeURIComponent(notificationId)}/read`,
          { method: 'PATCH' },
        ),
      );
    },
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InboxResponse>(queryKey);
      // v1's isCompleted is always server-derived false; never set it optimistically here.
      queryClient.setQueryData<InboxResponse>(queryKey, (current) => {
        if (!current) return current;
        const wasUnread = current.items.some((row) => row.id === item.id && !row.isRead);
        return {
          items: current.items.map((current_) =>
            current_.id === item.id ? { ...current_, isRead: true } : current_,
          ),
          unreadCount: wasUnread ? Math.max(0, current.unreadCount - 1) : current.unreadCount,
        };
      });
      return { previous };
    },
    onError: (_error, _item, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: (result) => {
      // demoMode/non-server rows never called the real API (mutationFn returned null above) --
      // invalidating here would refetch the static, unchanged demo fixture and stomp the
      // optimistic write right back to unread. Only a real PATCH response reaches past this.
      if (!result) return;
      // The PATCH response's unreadCount is actor-wide (services/api's mark_me_notification_read
      // deliberately computes it with audience=None) -- it must never be written into this
      // audience-filtered cache entry, or a dual-portal actor's other-portal unread rows would
      // leak into this portal's badge/drawer. Invalidate instead, so this exact audience-filtered
      // key refetches its own true, correctly-filtered count.
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useMarkAllInboxRead(activeContextId: string, audience: InboxAudience | undefined) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const queryKey = inboxKeys.list(activeContextId, audience);
  return useMutation({
    mutationFn: async () => {
      if (environment.demoMode) return null;
      return markResultSchema.parse(
        await environment.apiClient.request<unknown>('/api/v1/me/notifications/read-all', {
          method: 'POST',
        }),
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InboxResponse>(queryKey);
      queryClient.setQueryData<InboxResponse>(queryKey, (current) => {
        if (!current) return current;
        return {
          items: current.items.map((row) => (isServerRow(row) ? { ...row, isRead: true } : row)),
          unreadCount: 0,
        };
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: (result) => {
      if (!result) return;
      queryClient.setQueryData<InboxResponse>(queryKey, (current) =>
        current ? { ...current, unreadCount: result.unreadCount } : current,
      );
    },
  });
}
